import { Helmet } from 'react-helmet'
import { useState, ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import { AnimatePresence, motion } from 'framer-motion'

// Actions
import { actions as authActions } from 'modules/auth/actions'
import { actions as trackingActions } from 'modules/tracking/actions'

// Hooks
import { useWindowSize } from 'hooks/usewindowsize'
import { useSubscription } from 'modules/subscription/hooks'

// Components
import { Button, IconArrow, Image } from '@tracklib/kit'
import PageDots from 'components/onboard/pagedots'
import { Video } from 'components/onboard/interactiveguide'
import Markdown from 'components/base/markdown'

// Utils
import { isBrowser } from '@process'

import styles from './onboardmobile.css'

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }
  },
}

const FadeTransition = ({ children, id, animate }: { children: ReactNode; id: string; animate?: boolean }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={animate ? { opacity: 0 } : { opacity: 1 }}
    exit={animate ? {} : { opacity: 0 }}
    key={id}
    transition={{ duration: 0.125 }}
  >
    {children}
  </motion.div>
)

const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

const OnboardMobile = ({ redirect }: { redirect?: string }): JSX.Element => {
  const subscription = useSubscription()
  const isOnTrial = subscription?.state === 'TRIAL'
  const pages = [
    {
      title: 'It’s sampling time',
      description: `${
        isOnTrial ? '__Your trial has now started.__' : ''
      }Welcome to your free trial of Tracklib. You now have access to our full catalog of tracks, ready for you to loop, download, chop, sample and make your own.`,
      id: 1,
      media: '/static/images/onboard/mobile-onboarding-welcome.mp4',
    },
    {
      title: 'Create and download _perfect loops_',
      description:
        'Use the loop function to create and download seamless loops from any track. Try out different loops by tapping other parts of the song.',
      id: 2,
      media: '/static/images/onboard/mobile-onboarding-loop.mp4',
    },
    {
      title: 'Favorite, curate _and download_',
      description:
        'Save your favorite tracks, curate collections and download seamless loops or full tracks in master quality with only one click for a single credit.',
      id: 3,
      media: '/static/images/onboard/mobile-onboarding-favorite.mp4',
    },
    {
      title: 'Filter through _the crates_',
      description:
        'Use our detailed filters to find what you’re looking for or mix it up to discover the obscure and unexpected. This is where the real digging takes place.',
      id: 4,
      media: '/static/images/onboard/mobile-onboarding-filter.mp4',
    },
    {
      title: 'Let the music _guide you_',
      description:
        'Explore our specially curated catalog for inspiration or dig deep by browsing and filtering our vast library of tracks, artists, labels and genres.',
      id: 5,
      media: '/static/images/onboard/mobile-onboarding-browse-catalog.mp4',
    },
  ]

  const dispatch = useDispatch()
  const [[page, direction], setPage] = useState([0, 0])
  const { title, description, media } = pages[page]
  const isLastPage = page === pages.length - 1
  const isFirstPage = page === 0
  const { height = 0 } = useWindowSize()

  const paginate = (newDirection: number) => {
    const newPage = page + newDirection
    if (newPage < 0 || newPage >= pages.length) {
      return null
    }
    dispatch(trackingActions.trackevent('Onboarding paginate', { currentPage: page, newPage, type: 'mobile' }))
    return setPage([newPage, newDirection])
  }

  return (
    <>
      <Helmet>
        <body data-collapse="true" />
      </Helmet>
      <div className={styles.wrapper} style={isBrowser ? { height } : {}}>
        <aside className={styles.media}>
          {media.includes('.mp4') ? (
            <Video className={styles.video} mp4={media} key={media} cover="/static/images/onboard/videocover.jpg" />
          ) : (
            <Image image={media} absolute alt="" />
          )}
        </aside>
        <section className={styles.contentWrapper}>
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              className={styles.content}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              key={page}
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              custom={direction}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x)

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1)
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1)
                }
              }}
            >
              <h2 className={styles.title}>
                <Markdown>{title}</Markdown>
              </h2>
              <p className={styles.description}>
                <Markdown>{description}</Markdown>
              </p>
            </motion.div>
            {isLastPage && (
              <FadeTransition id="button">
                <Button
                  size="large"
                  to={redirect || '/inspiration'}
                  onClick={() => {
                    dispatch(trackingActions.trackevent('Finished onboarding'))
                    return dispatch(authActions.onboardingCompleted())
                  }}
                  className={styles.finishButton}
                >
                  Start browsing
                </Button>
              </FadeTransition>
            )}
          </AnimatePresence>
        </section>
        <nav className={styles.nav}>
          <FadeTransition id="prev" animate={isFirstPage}>
            <button onClick={() => paginate(-1)} className={styles.prev}>
              <IconArrow />
            </button>
          </FadeTransition>
          <FadeTransition id="dots" animate={isLastPage}>
            <PageDots pages={pages} current={page} />
          </FadeTransition>
          <FadeTransition id="next" animate={isLastPage}>
            <button onClick={() => paginate(1)} className={styles.next}>
              <IconArrow />
            </button>
          </FadeTransition>
        </nav>
      </div>
    </>
  )
}

export default OnboardMobile
